import {Box, Button, Checkbox, Heading, HStack, Popover, Text, VStack} from '@chakra-ui/react';
import {ListFilter} from 'lucide-react';
import {FileFilterFacet} from '../domain/fileFilters';
import {CountBadge} from './CountBadge';

interface FileFilterPopoverProps {
    facets: FileFilterFacet[];
    /** How many values are picked across every criterion (shown on the button). */
    selectedCount: number;
    onToggle: (criterionId: string, value: string) => void;
    onClear: () => void;
    /** Files shown out of those listed, for the summary line. */
    shownCount: number;
    totalCount: number;
}

/** The album grid's filters: a button opening every criterion's values as checkboxes. */
export function FileFilterPopover({facets, selectedCount, onToggle, onClear, shownCount, totalCount}: FileFilterPopoverProps) {
    const isActive = selectedCount > 0;
    return <Popover.Root positioning={{placement: 'bottom-end'}} lazyMount>
        <Popover.Trigger asChild>
            <Button
                size="sm"
                variant={isActive ? 'subtle' : 'outline'}
                colorPalette={isActive ? 'blue' : 'gray'}
                title={isActive ? `Szűrés: ${shownCount} / ${totalCount} elem látszik` : 'Szűrés'}
            >
                <ListFilter size={16}/>
                Szűrés
                <CountBadge count={selectedCount} size="sm" bg="blue.500"/>
            </Button>
        </Popover.Trigger>
        <Popover.Positioner>
            <Popover.Content width="xs" maxW="calc(100vw - 32px)">
                <Popover.Arrow />
                <Popover.Body p={3}>
                    <VStack align="stretch" gap={4} maxH="60vh" overflowY="auto">
                        {facets.map(({criterion, options}) =>
                            <Box key={criterion.id}>
                                <Heading as="h3" size="xs" mb={2}>{criterion.label}</Heading>
                                {options.length === 0
                                    ? <Text fontSize="sm" color="gray.500">Nincs választható érték.</Text>
                                    : <VStack align="stretch" gap={1.5}>
                                        {options.map((option) =>
                                            <Checkbox.Root
                                                key={option.value}
                                                colorPalette="blue"
                                                checked={option.selected}
                                                onCheckedChange={() => onToggle(criterion.id, option.value)}
                                            >
                                                <Checkbox.HiddenInput/>
                                                <Checkbox.Control/>
                                                <Checkbox.Label flex="1">
                                                    <HStack justify="space-between">
                                                        <Text as="span" color={option.count === 0 ? 'gray.400' : undefined}>
                                                            {option.label}
                                                        </Text>
                                                        <Text as="span" fontSize="xs" color="gray.500" css={{fontVariantNumeric: 'tabular-nums'}}>
                                                            {option.count}
                                                        </Text>
                                                    </HStack>
                                                </Checkbox.Label>
                                            </Checkbox.Root>
                                        )}
                                    </VStack>
                                }
                            </Box>
                        )}
                    </VStack>
                    <HStack justify="space-between" mt={3} pt={3} borderTopWidth="1px">
                        <Text fontSize="sm" color="gray.600">{shownCount} / {totalCount} elem</Text>
                        <Button size="xs" variant="ghost" onClick={onClear} disabled={!isActive}>Szűrők törlése</Button>
                    </HStack>
                </Popover.Body>
            </Popover.Content>
        </Popover.Positioner>
    </Popover.Root>;
}
